import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../auth/AuthContext";
import { usePermission } from "../../auth/usePermission";
import { formatMoneyBR, formatDateBR } from "../../utils/format";
import { formatDateInput, getMonthRange } from "../../utils/dateRange";

type IntegrityIssue = {
  issueId: string;
  severity: "CRITICA" | "AVISO";
  ruleCode: string;
  entityType: string;
  entityId: number;
  entityLabel: string;
  dateRef: string;
  amountRef: number;
  relatedType?: string;
  relatedId?: number;
  message: string;
  actionsAllowed: string[];
  bankAccountId?: number;
  cardId?: number;
};

type Summary = {
  total: number;
  critical: number;
  warnings: number;
  byRule: Record<string, number>;
};

type Option = { id: number; name: string };

type ActionModalState =
  | { type: "link"; issue: IntegrityIssue }
  | { type: "mark-manual"; issue: IntegrityIssue }
  | { type: "reverse"; issue: IntegrityIssue }
  | { type: "ignore"; issue: IntegrityIssue }
  | null;

type PaymentContext = {
  id: number;
  paymentDate: string | null;
  paidAmount: number;
  description: string;
  bankAccountId: number | null;
  cardId: number | null;
  account: {
    description: string | null;
    bankAccountId: number | null;
  } | null;
};

type SelectedPaymentDetails = PaymentContext & {
  bankAccountId: number | null;
};

type CreateMovementForm = {
  movementDate: string;
  amount: string;
  description: string;
  bankAccountId: string;
  cardId: string;
};

const tabs = [
  { label: "Visao geral", value: "" },
  { label: "CashFlowEntry", value: "CashFlowEntry" },
  { label: "AccountPayable", value: "AccountPayable" },
  { label: "Payment", value: "Payment" },
  { label: "AccountReceivable", value: "AccountReceivable" },
  { label: "Movement", value: "Movement" },
  { label: "Cartao (R07)", value: "CreditCardTransaction" },
];

export const FinanceIntegrityValidationPage: React.FC = () => {
  const { client } = useAuth();
  const canWriteMovement = usePermission("MOVEMENT_WRITE");
  const canWritePayable = usePermission("PAYABLE_WRITE");
  const canWriteReceivable = usePermission("RECEIVABLE_WRITE");

  const monthRange = useMemo(() => getMonthRange(new Date()), []);
  const [fromDate, setFromDate] = useState(formatDateInput(monthRange.from));
  const [toDate, setToDate] = useState(formatDateInput(monthRange.to));
  const [bankAccountId, setBankAccountId] = useState<string>("");
  const [cardId, setCardId] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [severity, setSeverity] = useState<string>("");
  const [ruleCode, setRuleCode] = useState<string>("");
  const [entityType, setEntityType] = useState<string>("");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [issues, setIssues] = useState<IntegrityIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [actionModal, setActionModal] = useState<ActionModalState>(null);
  const [selectedIssue, setSelectedIssue] = useState<IntegrityIssue | null>(null);
  const [selectedPaymentDetails, setSelectedPaymentDetails] =
    useState<SelectedPaymentDetails | null>(null);
  const [showCreateMovementModal, setShowCreateMovementModal] = useState(false);
  const [createMovementLoading, setCreateMovementLoading] = useState(false);
  const [createMovementLoadingIssueId, setCreateMovementLoadingIssueId] = useState<number | null>(
    null
  );
  const [createMovementRequiresBankAccount, setCreateMovementRequiresBankAccount] = useState(true);
  const [createMovementForm, setCreateMovementForm] = useState<CreateMovementForm>({
    movementDate: fromDate,
    amount: "",
    description: "",
    bankAccountId: "",
    cardId: "",
  });

  const [bankAccounts, setBankAccounts] = useState<Option[]>([]);
  const [cards, setCards] = useState<Option[]>([]);

  const [actionReason, setActionReason] = useState("");
  const [linkRelatedType, setLinkRelatedType] = useState("Movement");
  const [linkRelatedId, setLinkRelatedId] = useState("");

  const fetchOptions = useCallback(async () => {
    const [banksResponse, cardsResponse] = await Promise.all([
      client.get("/modules/bank-accounts?take=200&skip=0"),
      client.get("/modules/cards?take=200&skip=0"),
    ]);
    if (banksResponse.ok) {
      const payload = (await banksResponse.json()) as { data: Option[] };
      setBankAccounts(payload.data);
    }
    if (cardsResponse.ok) {
      const payload = (await cardsResponse.json()) as { data: Option[] };
      setCards(payload.data);
    }
  }, [client]);

  useEffect(() => {
    void fetchOptions();
  }, [fetchOptions]);

  const buildQuery = useCallback(
    (includePaging = true) => {
      const params = new URLSearchParams({
        from: fromDate,
        to: toDate,
      });
      if (bankAccountId) params.set("bankAccountId", bankAccountId);
      if (cardId) params.set("cardId", cardId);
      if (status) params.set("status", status);
      if (severity) params.set("severity", severity);
      if (ruleCode) params.set("ruleCode", ruleCode);
      if (entityType) params.set("entityType", entityType);
      if (includePaging) {
        params.set("take", String(pageSize));
        params.set("skip", String(page * pageSize));
      }
      return params;
    },
    [
      bankAccountId,
      cardId,
      entityType,
      fromDate,
      page,
      pageSize,
      ruleCode,
      severity,
      status,
      toDate,
    ]
  );

  const executeValidation = useCallback(async () => {
    if (!fromDate || !toDate) {
      setError("Periodo obrigatorio.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, issuesRes] = await Promise.all([
        client.get(`/audit/integrity/summary?${buildQuery(false).toString()}`),
        client.get(`/audit/integrity/issues?${buildQuery(true).toString()}`),
      ]);
      if (!summaryRes.ok || !issuesRes.ok) {
        throw new Error("Falha ao executar validacao.");
      }
      const summaryPayload = (await summaryRes.json()) as Summary;
      const issuesPayload = (await issuesRes.json()) as { items: IntegrityIssue[]; total: number };
      setSummary(summaryPayload);
      setIssues(issuesPayload.items);
      setTotal(issuesPayload.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Falha ao executar validacao.");
    } finally {
      setLoading(false);
    }
  }, [buildQuery, client, fromDate, toDate]);

  useEffect(() => {
    void executeValidation();
  }, [executeValidation]);

  const openModal = (type: Exclude<ActionModalState, null>["type"], issue: IntegrityIssue) => {
    setActionReason("");
    setLinkRelatedType(
      issue.entityType === "Movement" ? "AccountPayable" : "Movement"
    );
    setLinkRelatedId("");
    setActionModal({ type, issue } as ActionModalState);
  };

  const closeModal = () => setActionModal(null);

  const closeCreateMovementModal = () => {
    setCreateMovementLoading(false);
    setCreateMovementLoadingIssueId(null);
    setShowCreateMovementModal(false);
    setSelectedIssue(null);
    setSelectedPaymentDetails(null);
    setCreateMovementRequiresBankAccount(true);
    setCreateMovementForm({
      movementDate: fromDate,
      amount: "",
      description: "",
      bankAccountId: "",
      cardId: "",
    });
  };

  const reload = async () => {
    await executeValidation();
  };

  const downloadCsv = async () => {
    const response = await client.get(
      `/audit/integrity/export?${buildQuery(false).toString()}`
    );
    if (!response.ok) {
      setError("Falha ao exportar CSV.");
      return;
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "validacoes_financeiras.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleOpenCreateMovement = useCallback(
    async (issue: IntegrityIssue) => {
      setSelectedIssue(issue);
      setSelectedPaymentDetails(null);
      setShowCreateMovementModal(false);
      setCreateMovementRequiresBankAccount(true);
      setCreateMovementLoadingIssueId(issue.entityId);
      const prefillBankAccountId = issue.bankAccountId ? String(issue.bankAccountId) : "";
      const prefillCardId = issue.cardId ? String(issue.cardId) : "";
      setCreateMovementForm({
        movementDate: issue.dateRef ? issue.dateRef.slice(0, 10) : fromDate,
        amount: String(issue.amountRef),
        description: issue.entityLabel || issue.message,
        bankAccountId: prefillBankAccountId,
        cardId: prefillCardId,
      });

      if (issue.entityType !== "Payment") {
        setCreateMovementRequiresBankAccount(!prefillBankAccountId);
        setShowCreateMovementModal(true);
        setCreateMovementLoadingIssueId(null);
        return;
      }

      setCreateMovementLoading(true);
      try {
        const response = await client.get(`/audit/integrity/payments/${issue.entityId}`);
        if (!response.ok) {
          throw new Error("Falha ao carregar dados do pagamento.");
        }
        const payment = (await response.json()) as PaymentContext;
        const resolvedBankAccountId =
          payment.account?.bankAccountId ?? payment.bankAccountId ?? null;
        const resolvedDescription = payment.account?.description
          ? `Pagamento, ${payment.account.description}`
          : payment.description;

        setSelectedPaymentDetails({
          ...payment,
          bankAccountId: resolvedBankAccountId,
        });
        setCreateMovementRequiresBankAccount(!resolvedBankAccountId);
        setCreateMovementForm({
          movementDate: payment.paymentDate ?? "",
          amount: String(payment.paidAmount),
          description: resolvedDescription,
          bankAccountId: resolvedBankAccountId ? String(resolvedBankAccountId) : "",
          cardId: payment.cardId ? String(payment.cardId) : "",
        });
        setShowCreateMovementModal(true);
      } catch (err: unknown) {
        setError(
          err instanceof Error
            ? err.message
            : "Falha ao carregar dados do pagamento."
        );
      } finally {
        setCreateMovementLoading(false);
        setCreateMovementLoadingIssueId(null);
      }
    },
    [client, fromDate]
  );

  const handleAction = async () => {
    if (!actionModal) return;
    const { issue } = actionModal;
    setLoading(true);
    setError(null);
    try {
      if (actionModal.type === "link") {
        const response = await client.post("/audit/integrity/actions/link", {
          issueId: issue.issueId,
          entityType: issue.entityType,
          entityId: issue.entityId,
          relatedType: linkRelatedType,
          relatedId: Number(linkRelatedId),
        });
        if (!response.ok) throw new Error("Falha ao vincular.");
      }
      if (actionModal.type === "mark-manual") {
        const response = await client.post("/audit/integrity/actions/mark-manual", {
          issueId: issue.issueId,
          entityType: issue.entityType,
          entityId: issue.entityId,
          reason: actionReason,
        });
        if (!response.ok) throw new Error("Falha ao marcar manual.");
      }
      if (actionModal.type === "reverse") {
        const response = await client.post("/audit/integrity/actions/reverse", {
          issueId: issue.issueId,
          entityType: issue.entityType,
          entityId: issue.entityId,
          reason: actionReason,
        });
        if (!response.ok) throw new Error("Falha ao estornar.");
      }
      if (actionModal.type === "ignore") {
        const response = await client.post("/audit/integrity/actions/ignore", {
          issueId: issue.issueId,
          reason: actionReason,
        });
        if (!response.ok) throw new Error("Falha ao ignorar.");
      }
      setActionModal(null);
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Falha ao executar acao.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMovement = async () => {
    if (!selectedIssue) {
      return;
    }
    const amount = Number(createMovementForm.amount);
    if (Number.isNaN(amount)) {
      setError("Valor invalido.");
      return;
    }
    if (!createMovementForm.movementDate) {
      if (selectedIssue.entityType === "Payment") {
        console.warn(
          "[integrity] Payment sem paymentDate no criar movimento",
          selectedIssue.entityId
        );
      }
      setError("Data obrigatoria.");
      return;
    }
    if (createMovementRequiresBankAccount && !createMovementForm.bankAccountId) {
      setError("Conta bancaria obrigatoria.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await client.post("/audit/integrity/actions/create-movement", {
        issueId: selectedIssue.issueId,
        originType: selectedIssue.entityType === "Payment" ? "Payment" : selectedIssue.entityType,
        originId: selectedPaymentDetails?.id ?? selectedIssue.entityId,
        movementDate: createMovementForm.movementDate,
        amount,
        description:
          selectedIssue.entityType === "Payment"
            ? createMovementForm.description
            : createMovementForm.description || selectedIssue.message,
        bankAccountId: createMovementForm.bankAccountId
          ? Number(createMovementForm.bankAccountId)
          : undefined,
        cardId: createMovementForm.cardId ? Number(createMovementForm.cardId) : undefined,
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(payload.error || "Falha ao criar movimento.");
      }

      closeCreateMovementModal();
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Falha ao criar movimento.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!actionModal && !showCreateMovementModal && !createMovementLoading) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.classList.add("modal-open");
    document.body.style.overflow = "hidden";
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = previousOverflow;
    };
  }, [actionModal, showCreateMovementModal, createMovementLoading]);

  const issueActionsDisabled = (issue: IntegrityIssue, action: string) => {
    if (action === "CREATE_MOVEMENT") {
      return false;
    }
    if (!issue.actionsAllowed.includes(action)) {
      return true;
    }
    if (action === "LINK") {
      return !(canWritePayable || canWriteReceivable || canWriteMovement);
    }
    if (action === "REVERSE") {
      return !(canWritePayable || canWriteReceivable);
    }
    return false;
  };

  const canRenderCreateMovementModal =
    showCreateMovementModal &&
    selectedIssue &&
    (selectedIssue.entityType !== "Payment" || selectedPaymentDetails);

  const formatIssueDate = (value: string) => {
    if (!value) return "-";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return formatDateBR(`${value}T12:00:00`);
    }
    return formatDateBR(value);
  };

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <h1 className="mb-0">Validacoes financeiras</h1>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-outline-secondary" type="button" onClick={downloadCsv}>
            Exportar CSV
          </button>
          <button className="btn btn-outline-primary" type="button" onClick={reload}>
            Reprocessar vinculos
          </button>
          <button className="btn btn-primary" type="button" onClick={executeValidation}>
            Executar validacao
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">De</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Ate</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Conta bancaria</label>
              <select
                className="form-select"
                value={bankAccountId}
                onChange={(event) => setBankAccountId(event.target.value)}
              >
                <option value="">Todas</option>
                {bankAccounts.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Cartao</label>
              <select
                className="form-select"
                value={cardId}
                onChange={(event) => setCardId(event.target.value)}
              >
                <option value="">Todos</option>
                {cards.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">Todos</option>
                <option value="OPEN">Aberto</option>
                <option value="PAID">Pago</option>
                <option value="RECEIVED">Recebido</option>
                <option value="CANCELED">Cancelado</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Severidade</label>
              <select
                className="form-select"
                value={severity}
                onChange={(event) => setSeverity(event.target.value)}
              >
                <option value="">Todas</option>
                <option value="CRITICA">Criticas</option>
                <option value="AVISO">Avisos</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Regra</label>
              <select
                className="form-select"
                value={ruleCode}
                onChange={(event) => setRuleCode(event.target.value)}
              >
                <option value="">Todas</option>
                <option value="R01">R01 - Pagamento sem movimento</option>
                <option value="R02">R02 - Pago sem payment</option>
                <option value="R03">R03 - Recebido sem movimento</option>
                <option value="R04">R04 - Movimento sem origem</option>
                <option value="R05">R05 - Divergencia valor</option>
                <option value="R06">R06 - Cashflow sem movimento</option>
                <option value="R07">R07 - Cartao sem movimento</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Tipo</label>
              <select
                className="form-select"
                value={entityType}
                onChange={(event) => setEntityType(event.target.value)}
              >
                <option value="">Todos</option>
                {tabs
                  .filter((tab) => tab.value)
                  .map((tab) => (
                    <option key={tab.value} value={tab.value}>
                      {tab.label}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          {error && <div className="alert alert-danger mt-3">{error}</div>}
        </div>
      </div>

      {summary && (
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-4">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="text-muted">Criticas</div>
                <div className="fs-4 fw-semibold">{summary.critical}</div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="text-muted">Avisos</div>
                <div className="fs-4 fw-semibold">{summary.warnings}</div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="text-muted">Total</div>
                <div className="fs-4 fw-semibold">{summary.total}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ul className="nav nav-tabs mb-3">
        {tabs.map((tab) => (
          <li className="nav-item" key={tab.value || "all"}>
            <button
              type="button"
              className={`nav-link ${entityType === tab.value ? "active" : ""}`}
              onClick={() => {
                setEntityType(tab.value);
                setPage(0);
              }}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="card shadow-sm">
        <div className="card-body">
          {loading && <div className="alert alert-light">Carregando...</div>}
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Severidade</th>
                  <th>Regra</th>
                  <th>Entidade</th>
                  <th>Descricao</th>
                  <th>Data</th>
                  <th>Valor</th>
                  <th>Diagnostico</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue) => (
                  <tr key={issue.issueId}>
                    <td>
                      <span
                        className={`badge ${
                          issue.severity === "CRITICA" ? "bg-danger" : "bg-warning text-dark"
                        }`}
                      >
                        {issue.severity}
                      </span>
                    </td>
                    <td>{issue.ruleCode}</td>
                    <td>
                      {issue.entityType} #{issue.entityId}
                    </td>
                    <td>{issue.entityLabel}</td>
                    <td>{formatIssueDate(issue.dateRef)}</td>
                    <td>{formatMoneyBR(issue.amountRef)}</td>
                    <td>{issue.message}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          type="button"
                          onClick={() => openModal("link", issue)}
                          disabled={issueActionsDisabled(issue, "LINK")}
                        >
                          Vincular
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success"
                          type="button"
                          onClick={() => void handleOpenCreateMovement(issue)}
                          disabled={
                            createMovementLoading &&
                            createMovementLoadingIssueId === issue.entityId
                          }
                        >
                          {createMovementLoading &&
                          createMovementLoadingIssueId === issue.entityId ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                              />
                              Carregando...
                            </>
                          ) : (
                            "Criar movimento"
                          )}
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          type="button"
                          onClick={() => openModal("mark-manual", issue)}
                          disabled={issueActionsDisabled(issue, "MARK_MANUAL")}
                        >
                          Marcar manual
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          type="button"
                          onClick={() => openModal("reverse", issue)}
                          disabled={issueActionsDisabled(issue, "REVERSE")}
                        >
                          Estornar
                        </button>
                        <button
                          className="btn btn-sm btn-outline-dark"
                          type="button"
                          onClick={() => openModal("ignore", issue)}
                          disabled={issueActionsDisabled(issue, "IGNORE")}
                        >
                          Ignorar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {issues.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center">
                      Nenhuma pendencia encontrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              {total > 0 && (
                <>
                  {page * pageSize + 1} -{" "}
                  {Math.min(total, (page + 1) * pageSize)} de {total}
                </>
              )}
            </div>
            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 0}
                onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              >
                Anterior
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={(page + 1) * pageSize >= total}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Proximo
              </button>
            </div>
          </div>
        </div>
      </div>

      {actionModal &&
        createPortal(
          <>
            <div
              className="modal-backdrop fade show"
              style={{ zIndex: 1050 }}
              onClick={closeModal}
            />
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              style={{ zIndex: 1055 }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {actionModal.type === "link" && "Vincular registro"}
                      {actionModal.type === "mark-manual" && "Marcar manual"}
                      {actionModal.type === "reverse" && "Estornar"}
                      {actionModal.type === "ignore" && "Ignorar"}
                    </h5>
                    <button className="btn-close" onClick={closeModal} />
                  </div>
                  <div className="modal-body">
                    {actionModal.type === "link" && (
                      <div className="row g-2">
                        <div className="col-12 col-md-6">
                          <label className="form-label">Tipo relacionado</label>
                          <select
                            className="form-select"
                            value={linkRelatedType}
                            onChange={(event) => setLinkRelatedType(event.target.value)}
                          >
                            <option value="Movement">Movement</option>
                            <option value="AccountPayable">AccountPayable</option>
                            <option value="AccountReceivable">AccountReceivable</option>
                          </select>
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label">Id relacionado</label>
                          <input
                            className="form-control"
                            value={linkRelatedId}
                            onChange={(event) => setLinkRelatedId(event.target.value)}
                          />
                        </div>
                      </div>
                    )}
                    {["mark-manual", "reverse", "ignore"].includes(actionModal.type) && (
                      <div className="mb-2">
                        <label className="form-label">Motivo</label>
                        <textarea
                          className="form-control"
                          value={actionReason}
                          onChange={(event) => setActionReason(event.target.value)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-outline-secondary" onClick={closeModal}>
                      Cancelar
                    </button>
                    <button className="btn btn-primary" onClick={handleAction}>
                      Confirmar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
      {createMovementLoading &&
        selectedIssue?.entityType === "Payment" &&
        createPortal(
          <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1050 }} />
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              style={{ zIndex: 1055 }}
            >
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-body py-4 text-center">
                    <div className="spinner-border text-primary me-2" role="status" />
                    <span>Carregando dados do pagamento...</span>
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
      {canRenderCreateMovementModal &&
        selectedIssue &&
        createPortal(
          <>
            <div
              className="modal-backdrop fade show"
              style={{ zIndex: 1050 }}
              onClick={closeCreateMovementModal}
            />
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              style={{ zIndex: 1055 }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Criar movimento</h5>
                    <button className="btn-close" onClick={closeCreateMovementModal} />
                  </div>
                  <div className="modal-body">
                    <div className="row g-2">
                      <div className="col-12 col-md-4">
                        <label className="form-label">Data</label>
                        <input
                          className="form-control"
                          type="date"
                          value={createMovementForm.movementDate}
                          onChange={(event) =>
                            setCreateMovementForm((prev) => ({
                              ...prev,
                              movementDate: event.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Valor</label>
                        <input
                          className="form-control"
                          value={createMovementForm.amount}
                          onChange={(event) =>
                            setCreateMovementForm((prev) => ({
                              ...prev,
                              amount: event.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">
                          Conta bancaria
                          {createMovementRequiresBankAccount ? " *" : " (opcional)"}
                        </label>
                        <select
                          className="form-select"
                          value={createMovementForm.bankAccountId}
                          onChange={(event) =>
                            setCreateMovementForm((prev) => ({
                              ...prev,
                              bankAccountId: event.target.value,
                            }))
                          }
                        >
                          <option value="">Selecione...</option>
                          {bankAccounts.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Cartao (opcional)</label>
                        <select
                          className="form-select"
                          value={createMovementForm.cardId}
                          onChange={(event) =>
                            setCreateMovementForm((prev) => ({
                              ...prev,
                              cardId: event.target.value,
                            }))
                          }
                        >
                          <option value="">Selecione...</option>
                          {cards.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Descricao</label>
                        <input
                          className="form-control"
                          value={createMovementForm.description}
                          onChange={(event) =>
                            setCreateMovementForm((prev) => ({
                              ...prev,
                              description: event.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-outline-secondary" onClick={closeCreateMovementModal}>
                      Cancelar
                    </button>
                    <button className="btn btn-primary" onClick={handleCreateMovement}>
                      Confirmar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};
